import {
  ArrayInput,
  AutocompleteInput,
  DateInput,
  Edit,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
} from "@/components/admin";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { required, useRecordContext } from "ra-core";
import { Link } from "react-router-dom";
import { DCAddressFields } from './DCAddressFields';
import { AutoPopulateCompanyDetails } from '../shared/AutoPopulateCompanyDetails';
import { CHALLAN_TYPE_CHOICES, DELIVERY_TERMS_CHOICES, MODE_OF_TRANSPORT_CHOICES, UOM_CHOICES } from "../shared/companyConstants";
import { HSNFieldWrapper, HSNSuggestionsWrapper } from "../shared/HSNAutocomplete";
import { InventoryItemPickerWrapper } from "../shared/InventoryItemPicker";
import { FilteredContactInput } from "../../misc/FilteredContactInput";

const STATUS_CHOICES = [
  { id: "draft", name: "Draft" },
  { id: "sent", name: "Sent" },
  { id: "delivered", name: "Delivered" },
];

const DCEditTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <div className="flex items-center gap-3">
      <Button
        asChild
        variant="outline"
        size="sm"
        className="gap-1.5 bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 font-semibold cursor-pointer h-9 px-4 rounded-lg"
      >
        <Link to="/delivery_challans" className="flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4 text-[#00a68d]" /> Back
        </Link>
      </Button>
      <span className="text-xl font-bold tracking-tight">
        Edit Delivery Challan {record.reference}
      </span>
    </div>
  );
};

export const DCEdit = () => (
  <Edit title={<DCEditTitle />} mutationMode="pessimistic">
    <SimpleForm className="max-w-5xl mx-auto bg-transparent px-2 pb-16">
<AutoPopulateCompanyDetails />
      <div className="w-full space-y-6 bg-transparent">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full [&>*]:min-w-0">
          <TextInput
            source="reference"
            label="Challan No."
            disabled
            className="w-full opacity-70"
            helperText={false}
          />
          <DateInput source="challan_date" label="Challan Date" />
          <SelectInput
            source="challan_type"
            label="Challan Type"
            choices={CHALLAN_TYPE_CHOICES}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full [&>*]:min-w-0">
          <SelectInput
            source="mode_of_transport"
            label="Mode of Transport"
            choices={MODE_OF_TRANSPORT_CHOICES}
          />
          <TextInput source="vehicle_no" label="Vehicle No." />
          <SelectInput
            source="status"
            label="Status"
            choices={STATUS_CHOICES}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full [&>*]:min-w-0">
          <SelectInput
            source="delivery_terms"
            label="Delivery Terms"
            choices={DELIVERY_TERMS_CHOICES}
            helperText="Domestic transport or export/import (Incoterms)"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full [&>*]:min-w-0">
          <ReferenceInput source="company_id" reference="companies">
            <AutocompleteInput label="Bill To Company" />
          </ReferenceInput>
          <FilteredContactInput label="Contact" />
        </div>

        {/* X-3: removed dup "Contact Person Name" — the "Contact" dropdown above
            already identifies the person; keep Contact No. as an override. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full [&>*]:min-w-0">
          <TextInput source="contact_no" label="Contact No. (override)" helperText="Leave blank to use the selected contact's number" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full [&>*]:min-w-0">
          <ReferenceInput source="project_id" reference="projects">
            <AutocompleteInput label="Linked Project" />
          </ReferenceInput>
          <ReferenceInput source="pi_id" reference="proforma_invoices">
            <AutocompleteInput label="From PI" optionText="reference" />
          </ReferenceInput>
        </div>
        <DCAddressFields />

        <div className="w-full mt-4 pt-4 border-t border-slate-100">
          <h3 className="font-semibold mb-3 text-sm text-slate-800">Items</h3>
          <ArrayInput source="items" label="">
            <SimpleFormIterator className="flex flex-col gap-4">
              <div className="relative">
                <InventoryItemPickerWrapper setTaxRate={false} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start w-full [&>*]:min-w-0">
                <div className="md:col-span-2 relative">
                  <TextInput
                    source="description"
                    label="Product Description"
                    validate={required()}
                    helperText={false}
                    className="w-full"
                  />
                  <HSNSuggestionsWrapper />
                </div>
                <HSNFieldWrapper label="HSN / Item" />
                <TextInput
                  source="quantity"
                  label="Quantity"
                  helperText={false}
                />
                <SelectInput
                  source="unit"
                  label="UoM"
                  choices={UOM_CHOICES}
                  helperText={false}
                />
              </div>
              <div className="ml-4 border-l-2 border-slate-200 pl-4">
                <p className="text-xs text-muted-foreground mb-2 font-medium">
                  Sub-items
                </p>
                <ArrayInput source="sub_items" label="">
                  <SimpleFormIterator>
                    <TextInput
                      source="text"
                      label="Sub-item"
                      helperText={false}
                      className="w-full"
                    />
                    <TextInput
                      source="qty_note"
                      label="Qty note"
                      helperText={false}
                      className="w-40"
                    />
                  </SimpleFormIterator>
                </ArrayInput>
              </div>
            </SimpleFormIterator>
          </ArrayInput>
        </div>
      </div>
    </SimpleForm>
  </Edit>
);

